import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaFileExcel, FaFilePdf, FaSort, FaSortUp, FaSortDown,
  FaSearch, FaTrophy, FaHandshake, FaMoneyBillWave,
  FaCalendarCheck, FaChartBar, FaPrint, FaUserTie,
  FaHistory, FaTimes, FaReceipt, FaEye, FaCheckCircle, FaAward
} from 'react-icons/fa';
import logo from '../../Header/Images/RMB-Strip-Jaipur.png';
import PointRulesReferenceModal from '../Points/PointRulesReferenceModal';

interface MemberReport {
  id: number;
  name: string;
  email: string;
  number: string;
  company_name: string;
  designation: string;
  profile: string;
  r2rCount: number;
  businessGivenCount: number;
  businessGivenAmount: number;
  businessTakenCount: number;
  businessTakenAmount: number;
  referralsGivenCount: number;
  visitorsInvitedCount: number;
  totalPoints: number;
  attendancePoints?: number;
  activityPoints?: number;
  r2rPoints?: number;
  referralPoints?: number;
  visitorPoints?: number;
  fieldVisitPoints?: number;
  businessPoints?: number;
  presentCount: number;
  absentCount: number;
  substituteCount: number;
  totalMeetings: number;
  attendancePercentage: number;
}

type SortField = 'name' | 'company_name' | 'r2rCount' | 'businessGivenAmount' | 'businessTakenAmount' | 'totalPoints' | 'attendancePercentage' | 'absentCount';
type SortOrder = 'asc' | 'desc';

interface ReportsAnalyticsProps {
  isMemberPortal?: boolean;
}

const ReportsAnalytics: React.FC<ReportsAnalyticsProps> = ({ isMemberPortal = false }) => {
  const [reports, setReports] = useState<MemberReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [reportType, setReportType] = useState<'ALL' | 'R2R' | 'BUSINESS' | 'POINTS' | 'ATTENDANCE' | 'ABSENT' | 'INDUCTION'>('ALL');
  const [societyYear, setSocietyYear] = useState<string>('2026-2027');
  const [inductionLeaderboard, setInductionLeaderboard] = useState<any[]>([]);

  useEffect(() => {
    axios.get('https://rmbbackend.rmbjaipur.com/auth/reports/induction-leaderboard')
      .then(res => {
        if (res.data && res.data.success) {
          setInductionLeaderboard(res.data.leaderboard || []);
        }
      })
      .catch(err => console.error('Error fetching induction leaderboard:', err));
  }, []);

  // Member History & Verification Modal State
  const [selectedMember, setSelectedMember] = useState<MemberReport | null>(null);
  const [memberActivities, setMemberActivities] = useState<any[]>([]);
  const [memberAttendanceLogs, setMemberAttendanceLogs] = useState<any[]>([]);
  const [fetchingHistory, setFetchingHistory] = useState(false);
  const [historyCategoryFilter, setHistoryCategoryFilter] = useState<'ALL' | 'R2R' | 'BUSINESS' | 'REFERRAL' | 'VISITOR_INVITE' | 'FIELD_VISIT' | 'ATTENDANCE_LOGS'>('ALL');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [showRulesModal, setShowRulesModal] = useState(false);

  // Sorting & Theme State
  const [sortField, setSortField] = useState<SortField>('totalPoints');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [viewLayout, setViewLayout] = useState<'CARDS' | 'TABLE'>('CARDS');
  const [appTheme, setAppTheme] = useState<'dark' | 'light'>(() => {
    return (localStorage.getItem('APP_THEME') as 'dark' | 'light') || 'dark';
  });

  useEffect(() => {
    const checkTheme = () => {
      const current = (localStorage.getItem('APP_THEME') as 'dark' | 'light') || 'dark';
      setAppTheme(current);
    };
    window.addEventListener('storage', checkTheme);
    const interval = setInterval(checkTheme, 500);
    return () => {
      window.removeEventListener('storage', checkTheme);
      clearInterval(interval);
    };
  }, []);

  const fetchReports = async (sy = societyYear) => {
    setLoading(true);
    try {
      const res = await axios.get(`https://rmbbackend.rmbjaipur.com/auth/reports/analytics?society_year=${sy}`);
      if (res.data.success) {
        setReports(res.data.reports || []);
      }
    } catch (err) {
      console.error('Error fetching analytics reports:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports(societyYear);
  }, [societyYear]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return <FaSort className="text-slate-500 opacity-60 ml-1 inline" />;
    return sortOrder === 'asc' ? <FaSortUp className="text-[#FDBC12] ml-1 inline" /> : <FaSortDown className="text-[#FDBC12] ml-1 inline" />;
  };

  const filteredAndSortedReports = reports.filter((r) => {
    const q = searchQuery.toLowerCase();
    const nameMatch = (r.name || '').toString().toLowerCase().includes(q);
    const companyMatch = (r.company_name || '').toString().toLowerCase().includes(q);
    const numberMatch = (r.number !== undefined && r.number !== null) ? String(r.number).toLowerCase().includes(q) : false;
    return nameMatch || companyMatch || numberMatch;
  }).sort((a, b) => {
    let valA = a[sortField] ?? 0;
    let valB = b[sortField] ?? 0;

    if (typeof valA === 'string') {
      valA = valA.toLowerCase();
      valB = (valB as string).toLowerCase();
    }

    if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
    if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const exportToExcel = () => {
    const headers = ['Rank', 'Name', 'Company', 'Designation', 'Mobile', 'R2Rs', 'Business Given (INR)', 'Business Taken (INR)', 'Total Points', 'Attendance Pts', 'Activity Pts', 'Present Count', 'Absent Count', 'Total Meetings', 'Attendance %', 'Attendance Risk Status'];
    const csvRows = [headers.join(',')];

    filteredAndSortedReports.forEach((r, idx) => {
      const risk = r.attendancePercentage < 50 ? 'HIGH ABSENTEE RISK (<50%)' : (r.attendancePercentage < 75 ? 'WARNING (50-75%)' : 'GOOD ATTENDANCE (>75%)');
      const row = [
        idx + 1,
        `"${r.name}"`,
        `"${r.company_name || ''}"`,
        `"${r.designation || ''}"`,
        `"${r.number || ''}"`,
        r.r2rCount,
        r.businessGivenAmount,
        r.businessTakenCount,
        r.businessTakenAmount,
        r.totalPoints,
        r.attendancePoints || 0,
        r.activityPoints || 0,
        r.presentCount + r.substituteCount,
        r.absentCount || 0,
        r.totalMeetings,
        `${r.attendancePercentage}%`,
        `"${risk}"`
      ];
      csvRows.push(row.join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `RMB_Jaipur_Member_Analytics_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrintPDF = () => {
    window.print();
  };

  const handleOpenMemberHistory = async (member: MemberReport) => {
    setSelectedMember(member);
    setFetchingHistory(true);
    setMemberActivities([]);
    setMemberAttendanceLogs([]);
    setHistoryCategoryFilter('ALL');
    try {
      const res = await axios.get(`https://rmbbackend.rmbjaipur.com/auth/activities?member_id=${member.id}&society_year=${societyYear}`);
      setMemberActivities(res.data.activities || []);
      setMemberAttendanceLogs(res.data.attendance || []);
    } catch (err) {
      console.error('Error fetching member activity history:', err);
    } finally {
      setFetchingHistory(false);
    }
  };

  const totalAttendancePoints = memberAttendanceLogs.length > 0
    ? memberAttendanceLogs.reduce((acc, att) => {
      if (att.status === 'Present') return acc + 10;
      if (att.status === 'Late') return acc + 5;
      if (att.status === 'Substituted' || att.status === 'Substitute') return acc + 10;
      return acc;
    }, 0)
    : (selectedMember?.attendancePoints || 0);

  const totalActivityPoints = memberActivities.reduce((acc, act) => {
    if (act.status === 'VERIFIED') {
      if (act.type === 'R2R' && selectedMember) {
        const isHost = Number(act.giver_id) === Number(selectedMember.id);
        return acc + (isHost ? 5 : 10);
      }
      return acc + (Number(act.points_awarded) || 0);
    }
    return acc;
  }, 0);

  const displayTotalPoints = selectedMember ? selectedMember.totalPoints : (totalAttendancePoints + totalActivityPoints);

  const totalR2RPoints = memberActivities.reduce((acc, act) => {
    if (act.status === 'VERIFIED' && act.type === 'R2R' && selectedMember) {
      const isHost = Number(act.giver_id) === Number(selectedMember.id);
      return acc + (isHost ? 5 : 10);
    }
    return acc;
  }, 0);

  const totalReferralPoints = memberActivities.reduce((acc, act) => {
    if (act.status === 'VERIFIED' && act.type === 'REFERRAL') {
      return acc + (Number(act.points_awarded) || 0);
    }
    return acc;
  }, 0);

  const totalVisitorPoints = memberActivities.reduce((acc, act) => {
    if (act.status === 'VERIFIED' && act.type === 'VISITOR_INVITE') {
      return acc + (Number(act.points_awarded) || 0);
    }
    return acc;
  }, 0);

  const totalFieldVisitPoints = memberActivities.reduce((acc, act) => {
    if (act.status === 'VERIFIED' && act.type === 'FIELD_VISIT') {
      return acc + (Number(act.points_awarded) || 0);
    }
    return acc;
  }, 0);

  const totalBusinessPoints = memberActivities.reduce((acc, act) => {
    if (act.status === 'VERIFIED' && act.type === 'BUSINESS_ACK') {
      return acc + (Number(act.points_awarded) || 0);
    }
    return acc;
  }, 0);

  const combinedHistoryItems = [
    ...memberActivities.filter((act) => {
      if (!selectedMember) return false;
      const mId = Number(selectedMember.id);
      return Number(act.giver_id) === mId || Number(act.receiver_id) === mId;
    }).map((act) => {
      const mId = Number(selectedMember?.id);
      const isVisitor = Number(act.giver_id) === mId;
      const awarded = act.type === 'R2R' ? (isVisitor ? 10 : 5) : (Number(act.points_awarded) || 0);
      return {
        ...act,
        points_awarded: awarded
      };
    }),
    ...memberAttendanceLogs.map((att) => {
      const pts = att.points_awarded ?? (att.status === 'Present' ? 10 : att.status === 'Late' ? 5 : (att.status === 'Substituted' || att.status === 'Substitute') ? 10 : 0);
      return {
        id: `att_${att.id || att.event_id}`,
        type: 'ATTENDANCE_LOGS',
        isAttendanceRecord: true,
        giver_id: att.user_id,
        giver_name: selectedMember?.name,
        receiver_name: att.event_title || `Chapter Event #${att.event_id}`,
        date: att.event_date ? new Date(att.event_date).toISOString().split('T')[0] : (att.created_at ? new Date(att.created_at).toISOString().split('T')[0] : 'N/A'),
        amount: 0,
        status: 'VERIFIED',
        points_awarded: pts,
        attendance_status: att.status || 'Present',
        details: att.substitute_name ? `Substitute: ${att.substitute_name} (${att.substitute_company || 'N/A'})` : (att.notes || `Meeting Attendance Status: ${att.status || 'Present'}`)
      };
    })
  ].sort((a, b) => new Date(b.date || b.created_at || 0).getTime() - new Date(a.date || a.created_at || 0).getTime());

  const filteredHistoryActivities = combinedHistoryItems.filter((item) => {
    if (historyCategoryFilter === 'ALL') return true;
    if (historyCategoryFilter === 'ATTENDANCE_LOGS') return item.isAttendanceRecord || item.type === 'ATTENDANCE_LOGS';
    return item.type === historyCategoryFilter;
  });

  return (
    <div className="p-6 bg-[#131E32] min-h-screen text-slate-100 space-y-6">

      <div className="bg-[#1E2B45] p-5 rounded-2xl border border-[#2A3B5C] flex flex-col md:flex-row justify-between items-start md:items-center gap-4 shadow-xl">
        <div className="flex items-center gap-4">
          <div className="bg-white px-3.5 py-1.5 rounded-xl shadow-md border border-slate-200 flex items-center justify-center">
            <img src={logo} alt="RMB Logo" className="h-8 object-contain" />
          </div>
          <div>
            <h1 className="text-xl font-black text-white flex items-center gap-2">
              Reports & Analytics Center
            </h1>
            <p className="text-xs text-slate-400">
              Max R2Rs, Business Givers & Takers, Leaderboard Points, and Itemized Activity History Verification.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowRulesModal(true)}
            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-black text-xs rounded-xl shadow flex items-center gap-2 transition cursor-pointer"
          >
            <FaAward className="text-[#FDBC12]" size={15} /> Points Rules Matrix
          </button>
          <button
            onClick={exportToExcel}
            className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs rounded-xl shadow flex items-center gap-2 transition cursor-pointer"
          >
            <FaFileExcel size={15} /> Export Excel (.CSV)
          </button>
          <button
            onClick={handlePrintPDF}
            className="px-4 py-2.5 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-xs rounded-xl shadow flex items-center gap-2 transition cursor-pointer"
          >
            <FaPrint size={15} /> Print / Save PDF
          </button>
        </div>
      </div>

      <div className="bg-[#1E2B45] p-4 rounded-xl border border-[#2A3B5C] flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <div className="relative w-full sm:w-72">
            <FaSearch className="absolute left-3 top-3 text-slate-400 text-xs" />
            <input
              type="text"
              placeholder="Search member by name or company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-[#131E32] border border-[#2A3B5C] focus:border-[#FDBC12] rounded-lg text-xs text-white outline-none font-bold"
            />
          </div>

          {isMemberPortal ? (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-amber-400 font-extrabold flex items-center gap-1 whitespace-nowrap">
                📅 Society Year:
              </span>
              <span className="px-3 py-1.5 bg-amber-500/20 border border-amber-500/40 text-amber-300 font-black rounded-lg text-xs flex items-center gap-1.5 shadow">
                🔒 2026 - 2027 (Current Society Year)
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-amber-400 font-extrabold flex items-center gap-1 whitespace-nowrap">
                📅 Society Year:
              </span>
              <select
                value={societyYear}
                onChange={(e) => setSocietyYear(e.target.value)}
                className="px-3 py-2 bg-[#131E32] border border-amber-500/40 text-amber-300 font-black rounded-lg outline-none cursor-pointer focus:ring-2 focus:ring-amber-500 text-xs"
              >
                <option value="all">🌟 All Time History</option>
                <option value="2026-2027">📅 2026 - 2027 Society Year</option>
                <option value="2025-2026">📅 2025 - 2026 Society Year</option>
                <option value="2024-2025">📅 2024 - 2025 Society Year</option>
              </select>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 text-xs font-bold w-full sm:w-auto justify-end">
          {/* Layout Mode Switcher */}
          <div className="flex items-center bg-[#131E32] p-1 rounded-xl border border-[#2A3B5C]">
            <button
              type="button"
              onClick={() => setViewLayout('CARDS')}
              className={`px-3 py-1 rounded-lg text-xs font-black transition cursor-pointer flex items-center gap-1.5 ${viewLayout === 'CARDS'
                  ? 'bg-[#FDBC12] text-[#0A2451] shadow'
                  : 'text-slate-300 hover:text-white'
                }`}
            >
              📄 Executive Cards
            </button>
            <button
              type="button"
              onClick={() => setViewLayout('TABLE')}
              className={`px-3 py-1 rounded-lg text-xs font-black transition cursor-pointer flex items-center gap-1.5 ${viewLayout === 'TABLE'
                  ? 'bg-[#FDBC12] text-[#0A2451] shadow'
                  : 'text-slate-300 hover:text-white'
                }`}
            >
              📊 Table View
            </button>
          </div>

          <span className="text-slate-400 whitespace-nowrap hidden lg:inline">Report View:</span>
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar max-w-full py-1">
            {[
              { id: 'ALL', label: '📊 All Metrics' },
              { id: 'INDUCTION', label: '🤝 Max Induction Report' },
              { id: 'ABSENT', label: '❌ Absenteeism Report' },
              { id: 'ATTENDANCE', label: '📅 Attendance Count' },
              { id: 'R2R', label: '🤝 Max R2R' },
              { id: 'BUSINESS', label: '💰 Business Givers & Takers' },
              { id: 'POINTS', label: '🏆 Leaderboard Points' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setReportType(tab.id as any);
                  if (tab.id === 'ABSENT') { setSortField('absentCount'); setSortOrder('desc'); }
                  else if (tab.id === 'R2R') { setSortField('r2rCount'); setSortOrder('desc'); }
                  else if (tab.id === 'BUSINESS') { setSortField('businessGivenAmount'); setSortOrder('desc'); }
                  else if (tab.id === 'POINTS') { setSortField('totalPoints'); setSortOrder('desc'); }
                  else if (tab.id === 'ATTENDANCE') { setSortField('attendancePercentage'); setSortOrder('desc'); }
                }}
                className={`px-3 py-1.5 rounded-lg transition whitespace-nowrap cursor-pointer flex-shrink-0 ${reportType === tab.id
                    ? 'bg-[#FDBC12] text-[#0A2451] font-black shadow'
                    : 'bg-[#131E32] text-slate-300 border border-[#2A3B5C] hover:text-white'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ANALYTICS SUMMARY STATS CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
        <div className={`p-4 rounded-xl border flex items-center justify-between transition-colors ${appTheme === 'light' ? 'bg-white border-slate-200 shadow-sm' : 'bg-[#1E2B45] border-[#2A3B5C]'
          }`}>
          <div>
            <p className={`text-[11px] font-bold uppercase ${appTheme === 'light' ? 'text-slate-500' : 'text-slate-400'}`}>Absenteeism Risk</p>
            <p className="text-lg font-black text-rose-500">
              {reports.filter(r => r.attendancePercentage < 50).length} High Risk
            </p>
            <p className="text-xs text-rose-400 font-bold">
              {reports.reduce((sum, r) => sum + (r.absentCount || 0), 0)} Total Absences
            </p>
          </div>
          <div className="p-3 bg-rose-500/20 text-rose-500 rounded-xl">
            <FaTimes size={22} />
          </div>
        </div>

        <div className={`p-4 rounded-xl border flex items-center justify-between transition-colors ${appTheme === 'light' ? 'bg-white border-slate-200 shadow-sm' : 'bg-[#1E2B45] border-[#2A3B5C]'
          }`}>
          <div>
            <p className={`text-[11px] font-bold uppercase ${appTheme === 'light' ? 'text-slate-500' : 'text-slate-400'}`}>Top R2R Champion</p>
            <p className={`text-lg font-black truncate max-w-[150px] ${appTheme === 'light' ? 'text-slate-900' : 'text-white'}`}>
              {reports.sort((a, b) => b.r2rCount - a.r2rCount)[0]?.name || '-'}
            </p>
            <p className="text-xs text-amber-600 font-bold">
              {reports.sort((a, b) => b.r2rCount - a.r2rCount)[0]?.r2rCount || 0} R2Rs Completed
            </p>
          </div>
          <div className="p-3 bg-blue-500/20 text-blue-600 rounded-xl">
            <FaHandshake size={22} />
          </div>
        </div>

        <div className={`p-4 rounded-xl border flex items-center justify-between transition-colors ${appTheme === 'light' ? 'bg-white border-slate-200 shadow-sm' : 'bg-[#1E2B45] border-[#2A3B5C]'
          }`}>
          <div>
            <p className={`text-[11px] font-bold uppercase ${appTheme === 'light' ? 'text-slate-500' : 'text-slate-400'}`}>Top Business Giver</p>
            <p className={`text-lg font-black truncate max-w-[150px] ${appTheme === 'light' ? 'text-slate-900' : 'text-white'}`}>
              {reports.sort((a, b) => b.businessGivenAmount - a.businessGivenAmount)[0]?.name || '-'}
            </p>
            <p className="text-xs text-emerald-600 font-bold">
              ₹{(reports.sort((a, b) => b.businessGivenAmount - a.businessGivenAmount)[0]?.businessGivenAmount || 0).toLocaleString('en-IN')}
            </p>
          </div>
          <div className="p-3 bg-emerald-500/20 text-emerald-600 rounded-xl">
            <FaMoneyBillWave size={22} />
          </div>
        </div>

        <div className={`p-4 rounded-xl border flex items-center justify-between transition-colors ${appTheme === 'light' ? 'bg-white border-slate-200 shadow-sm' : 'bg-[#1E2B45] border-[#2A3B5C]'
          }`}>
          <div>
            <p className={`text-[11px] font-bold uppercase ${appTheme === 'light' ? 'text-slate-500' : 'text-slate-400'}`}>Top Business Receiver</p>
            <p className={`text-lg font-black truncate max-w-[150px] ${appTheme === 'light' ? 'text-slate-900' : 'text-white'}`}>
              {reports.sort((a, b) => b.businessTakenAmount - a.businessTakenAmount)[0]?.name || '-'}
            </p>
            <p className="text-xs text-purple-600 font-bold">
              ₹{(reports.sort((a, b) => b.businessTakenAmount - a.businessTakenAmount)[0]?.businessTakenAmount || 0).toLocaleString('en-IN')}
            </p>
          </div>
          <div className="p-3 bg-purple-500/20 text-purple-600 rounded-xl">
            <FaUserTie size={22} />
          </div>
        </div>

        <div className={`p-4 rounded-xl border flex items-center justify-between transition-colors ${appTheme === 'light' ? 'bg-white border-slate-200 shadow-sm' : 'bg-[#1E2B45] border-[#2A3B5C]'
          }`}>
          <div>
            <p className={`text-[11px] font-bold uppercase ${appTheme === 'light' ? 'text-slate-500' : 'text-slate-400'}`}>Points Leader #1</p>
            <p className={`text-lg font-black truncate max-w-[150px] ${appTheme === 'light' ? 'text-slate-900' : 'text-white'}`}>
              {reports.sort((a, b) => b.totalPoints - a.totalPoints)[0]?.name || '-'}
            </p>
            <p className="text-xs text-amber-600 font-bold">
              🏆 {reports.sort((a, b) => b.totalPoints - a.totalPoints)[0]?.totalPoints || 0} Points
            </p>
          </div>
          <div className="p-3 bg-amber-500/20 text-amber-600 rounded-xl">
            <FaTrophy size={22} />
          </div>
        </div>
      </div>

      {/* MASTER ANALYTICS CARDS OR TABLE */}
      {reportType === 'INDUCTION' ? (
        <div className="space-y-4">
          <div className="bg-[#1E2B45] p-5 rounded-2xl border border-[#2A3B5C] shadow-xl">
            <h2 className="text-lg font-black text-amber-400 flex items-center gap-2 mb-1">
              🏆 Max Induction Leaderboard
            </h2>
            <p className="text-xs text-slate-300">
              Members ranked by the highest number of new members inducted into RMB Jaipur Chapter.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {inductionLeaderboard.length === 0 ? (
              <div className="col-span-full bg-[#1E2B45] p-8 rounded-2xl border border-[#2A3B5C] text-center text-slate-400 font-bold">
                No member induction records found yet. Set "Induction By" in Member Edit to record inductions.
              </div>
            ) : (
              inductionLeaderboard.map((item, idx) => (
                <div key={item.id} className="bg-[#1E2B45] p-5 rounded-2xl border border-[#2A3B5C] shadow-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="px-3 py-1 bg-amber-500/20 text-[#FDBC12] border border-amber-500/40 rounded-xl font-black text-xs">
                      {idx === 0 ? '🥇 Rank #1 Inductor' : idx === 1 ? '🥈 Rank #2 Inductor' : idx === 2 ? '🥉 Rank #3 Inductor' : `Rank #${idx + 1}`}
                    </span>
                    <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 px-3 py-1 rounded-xl text-xs font-black">
                      🤝 {item.total_inducted} Inductions
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    <img
                      src={item.profile ? (item.profile.startsWith('http') ? item.profile : `https://rmbbackend.rmbjaipur.com/${item.profile.replace(/\\/g, '/')}`) : 'https://via.placeholder.com/48'}
                      alt={item.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-amber-400"
                    />
                    <div>
                      <h3 className="font-extrabold text-white text-sm">{item.name}</h3>
                      <p className="text-xs text-slate-400">{item.company_name || 'RMB Member'}</p>
                      <p className="text-[11px] text-amber-300 font-medium">{item.business_classification || item.designation || 'Member'}</p>
                    </div>
                  </div>

                  {item.inducted_members && (
                    <div className="bg-[#131E32] p-3 rounded-xl border border-[#2A3B5C] text-xs">
                      <p className="font-extrabold text-slate-300 mb-1">Inducted Members:</p>
                      <p className="text-slate-400 text-[11px] leading-relaxed">
                        {item.inducted_members.split(';').map((mStr: string) => mStr.split(':')[1] || mStr).join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      ) : viewLayout === 'CARDS' ? (
        <div className="space-y-3.5">
          {loading ? (
            <div className="bg-[#1E2B45] p-10 rounded-2xl border border-[#2A3B5C] text-center text-slate-400 font-bold">
              Generating reports analytics...
            </div>
          ) : filteredAndSortedReports.length === 0 ? (
            <div className="bg-[#1E2B45] p-10 rounded-2xl border border-[#2A3B5C] text-center text-slate-400 font-bold">
              No member record found matching filter criteria.
            </div>
          ) : (
            filteredAndSortedReports.map((r, idx) => (
              <div
                key={r.id}
                className={`rounded-2xl p-4 sm:p-5 border shadow-md hover:shadow-xl transition-all flex flex-wrap xl:flex-nowrap items-center justify-between gap-4 sm:gap-6 ${appTheme === 'light' ? 'bg-white border-slate-200 text-slate-900' : 'bg-[#1E2B45] border-[#2A3B5C] text-white'
                  }`}
              >
                {/* 1. Rank Badge, Avatar & Member Info (Fixed Uniform Width) */}
                <div className="flex items-center gap-3.5 w-full md:w-[260px] lg:w-[290px] flex-shrink-0">
                  <div className="flex-shrink-0 text-center w-10">
                    {idx === 0 ? (
                      <span className="px-2.5 py-1 rounded-xl bg-amber-500/20 text-[#FDBC12] border border-amber-500/40 font-black text-xs shadow-xs inline-block">🥇 #1</span>
                    ) : idx === 1 ? (
                      <span className={`px-2.5 py-1 rounded-xl font-black text-xs shadow-xs border inline-block ${appTheme === 'light' ? 'bg-slate-100 text-slate-800 border-slate-300' : 'bg-slate-700/60 text-slate-200 border-slate-500/40'
                        }`}>🥈 #2</span>
                    ) : idx === 2 ? (
                      <span className={`px-2.5 py-1 rounded-xl font-black text-xs shadow-xs border inline-block ${appTheme === 'light' ? 'bg-amber-50 text-amber-900 border-amber-300' : 'bg-amber-900/40 text-amber-300 border-amber-700/40'
                        }`}>🥉 #3</span>
                    ) : (
                      <span className={`px-2 py-1 rounded-xl font-bold text-xs border inline-block ${appTheme === 'light' ? 'bg-slate-50 text-slate-500 border-slate-200' : 'bg-[#131E32] text-slate-400 border-[#2A3B5C]'
                        }`}>#{idx + 1}</span>
                    )}
                  </div>

                  <img
                    src={r.profile ? (r.profile.startsWith('http') ? r.profile : `https://rmbbackend.rmbjaipur.com/${r.profile.replace(/\\/g, '/')}`) : 'https://via.placeholder.com/48'}
                    alt={r.name}
                    className="w-11 h-11 rounded-full object-cover border-2 border-[#FDBC12] shadow-md flex-shrink-0"
                    onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/48'; }}
                  />

                  <div className="min-w-0 flex-1">
                    <h3 className={`font-extrabold text-sm tracking-wide truncate ${appTheme === 'light' ? 'text-slate-900' : 'text-white'}`} title={r.name}>{r.name}</h3>
                    <p className={`text-xs font-semibold truncate ${appTheme === 'light' ? 'text-slate-600' : 'text-slate-400'}`} title={r.designation}>{r.designation || 'Rotary Member'}</p>
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded-md text-[10px] font-bold border truncate max-w-full ${appTheme === 'light' ? 'bg-slate-100 text-slate-700 border-slate-200' : 'bg-[#131E32] text-slate-300 border-[#2A3B5C]'
                      }`} title={r.company_name}>
                      🏢 {r.company_name || 'RMB Jaipur'}
                    </span>
                  </div>
                </div>

                {/* 2. Key Performance Metrics Grid (Uniform Strict 4-Column Layout) */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 text-xs font-bold flex-1 w-full min-w-0">
                  {/* R2R Count */}
                  <div className={`px-3 py-2 rounded-xl border flex items-center gap-2 shadow-xs min-w-0 ${appTheme === 'light' ? 'bg-indigo-50 text-indigo-950 border-indigo-200' : 'bg-indigo-950/80 text-indigo-200 border-indigo-500/40'
                    }`}>
                    <span className={`p-1.5 rounded-lg flex-shrink-0 ${appTheme === 'light' ? 'bg-indigo-200 text-indigo-800' : 'bg-indigo-900/80 text-indigo-300'}`}><FaHandshake size={13} /></span>
                    <div className="min-w-0">
                      <div className={`text-[9px] uppercase tracking-wider font-extrabold ${appTheme === 'light' ? 'text-indigo-700' : 'text-indigo-400'}`}>R2Rs</div>
                      <div className={`font-black text-xs truncate ${appTheme === 'light' ? 'text-indigo-950' : 'text-white'}`}>{r.r2rCount} Completed</div>
                    </div>
                  </div>

                  {/* Business Given */}
                  <div className={`px-3 py-2 rounded-xl border flex items-center gap-2 shadow-xs min-w-0 ${appTheme === 'light' ? 'bg-emerald-50 text-emerald-950 border-emerald-200' : 'bg-emerald-950/80 text-emerald-200 border-emerald-500/40'
                    }`}>
                    <span className={`p-1.5 rounded-lg flex-shrink-0 ${appTheme === 'light' ? 'bg-emerald-200 text-emerald-800' : 'bg-emerald-900/80 text-emerald-300'}`}><FaMoneyBillWave size={13} /></span>
                    <div className="min-w-0">
                      <div className={`text-[9px] uppercase tracking-wider font-extrabold ${appTheme === 'light' ? 'text-emerald-700' : 'text-emerald-400'}`}>Given (₹)</div>
                      <div className={`font-black text-xs truncate ${appTheme === 'light' ? 'text-emerald-900' : 'text-emerald-300'}`}>₹{r.businessGivenAmount.toLocaleString('en-IN')}</div>
                    </div>
                  </div>

                  {/* Business Taken */}
                  <div className={`px-3 py-2 rounded-xl border flex items-center gap-2 shadow-xs min-w-0 ${appTheme === 'light' ? 'bg-purple-50 text-purple-950 border-purple-200' : 'bg-purple-950/80 text-purple-200 border-purple-500/40'
                    }`}>
                    <span className={`p-1.5 rounded-lg flex-shrink-0 ${appTheme === 'light' ? 'bg-purple-200 text-purple-800' : 'bg-purple-900/80 text-purple-300'}`}><FaUserTie size={13} /></span>
                    <div className="min-w-0">
                      <div className={`text-[9px] uppercase tracking-wider font-extrabold ${appTheme === 'light' ? 'text-purple-700' : 'text-purple-400'}`}>Received (₹)</div>
                      <div className={`font-black text-xs truncate ${appTheme === 'light' ? 'text-purple-900' : 'text-purple-300'}`}>₹{r.businessTakenAmount.toLocaleString('en-IN')}</div>
                    </div>
                  </div>

                  {/* Attendance Rate */}
                  <div className={`px-3 py-2 rounded-xl border flex items-center gap-2 shadow-xs min-w-0 ${appTheme === 'light' ? 'bg-slate-100 text-slate-900 border-slate-200' : 'bg-[#131E32] text-slate-200 border-[#2A3B5C]'
                    }`}>
                    <span className={`p-1.5 rounded-lg flex-shrink-0 ${appTheme === 'light' ? 'bg-slate-200 text-slate-700' : 'bg-slate-800 text-slate-300'}`}><FaCalendarCheck size={13} /></span>
                    <div className="min-w-0">
                      <div className={`text-[9px] uppercase tracking-wider font-extrabold ${appTheme === 'light' ? 'text-slate-500' : 'text-slate-400'}`}>Attendance</div>
                      <div className={`font-black text-xs truncate ${appTheme === 'light' ? 'text-slate-900' : 'text-white'}`}>{r.attendancePercentage}% ({r.presentCount + r.substituteCount}/{r.totalMeetings})</div>
                    </div>
                  </div>
                </div>

                {/* 3. Points Scorecard & Action Button */}
                <div className={`flex items-center gap-3.5 flex-shrink-0 w-full xl:w-auto justify-between xl:justify-end border-t xl:border-t-0 pt-3 xl:pt-0 ${appTheme === 'light' ? 'border-slate-100' : 'border-[#2A3B5C]'
                  }`}>
                  <div className={`px-3.5 py-2 rounded-xl border text-center shadow-xs min-w-[100px] ${appTheme === 'light' ? 'bg-amber-50 border-amber-300 text-amber-950' : 'bg-amber-950/90 border-amber-500/40 text-[#FDBC12]'
                    }`}>
                    <div className={`text-xs font-black flex items-center justify-center gap-1 tracking-wide ${appTheme === 'light' ? 'text-amber-900' : 'text-[#FDBC12]'
                      }`}>
                      🏆 {r.totalPoints} PTS
                    </div>
                    <div className="text-[10px] font-bold mt-0.5 space-x-1">
                      <span className={appTheme === 'light' ? 'text-amber-800' : 'text-amber-300'} title="Attendance Points">Att: {r.attendancePoints || 0}</span>
                      <span className={appTheme === 'light' ? 'text-slate-400' : 'text-slate-500'}>•</span>
                      <span className={appTheme === 'light' ? 'text-blue-700' : 'text-blue-300'} title="Activity Points">Act: {r.activityPoints || 0}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => handleOpenMemberHistory(r)}
                    className="px-4 py-2.5 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-xs rounded-xl shadow-lg transition flex items-center gap-1.5 cursor-pointer flex-shrink-0"
                  >
                    <FaHistory /> View Details
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className={`rounded-2xl border overflow-hidden transition-colors ${appTheme === 'light' ? 'bg-white border-slate-200 shadow-md text-slate-900' : 'bg-[#1E2B45] border-[#2A3B5C] shadow-2xl text-white'
          }`}>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className={`font-extrabold uppercase border-b select-none ${appTheme === 'light' ? 'bg-slate-100 text-slate-800 border-slate-200' : 'bg-[#0A2451] text-slate-300 border-[#2A3B5C]'
                  }`}>
                  <th className="p-3">Rank</th>
                  <th
                    className="p-3 cursor-pointer transition"
                    onClick={() => handleSort('name')}
                  >
                    Member Name {renderSortIcon('name')}
                  </th>
                  <th
                    className="p-3 cursor-pointer transition"
                    onClick={() => handleSort('company_name')}
                  >
                    Company Name {renderSortIcon('company_name')}
                  </th>

                  {(reportType === 'ALL' || reportType === 'R2R') && (
                    <th
                      className="p-3 cursor-pointer transition text-center"
                      onClick={() => handleSort('r2rCount')}
                    >
                      🤝 R2R Count {renderSortIcon('r2rCount')}
                    </th>
                  )}

                  {(reportType === 'ALL' || reportType === 'BUSINESS') && (
                    <>
                      <th
                        className="p-3 cursor-pointer transition text-right"
                        onClick={() => handleSort('businessGivenAmount')}
                      >
                        💰 Business Given (₹) {renderSortIcon('businessGivenAmount')}
                      </th>
                      <th
                        className="p-3 cursor-pointer transition text-right"
                        onClick={() => handleSort('businessTakenAmount')}
                      >
                        📥 Business Taken (₹) {renderSortIcon('businessTakenAmount')}
                      </th>
                    </>
                  )}

                  {(reportType === 'ALL' || reportType === 'POINTS') && (
                    <th
                      className="p-3 cursor-pointer transition text-center"
                      onClick={() => handleSort('totalPoints')}
                    >
                      🏆 Total Points {renderSortIcon('totalPoints')}
                    </th>
                  )}

                  {(reportType === 'ALL' || reportType === 'ATTENDANCE') && (
                    <>
                      <th className="p-3 text-center">Meeting Attendance (Attended / Total)</th>
                      <th
                        className="p-3 cursor-pointer transition text-center"
                        onClick={() => handleSort('attendancePercentage')}
                      >
                        📅 Attendance % {renderSortIcon('attendancePercentage')}
                      </th>
                    </>
                  )}
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className={`divide-y ${appTheme === 'light' ? 'divide-slate-200' : 'divide-[#2A3B5C]'}`}>
                {loading ? (
                  <tr>
                    <td colSpan={11} className="p-10 text-center text-slate-400 font-bold">Generating reports analytics...</td>
                  </tr>
                ) : filteredAndSortedReports.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="p-10 text-center text-slate-400 font-bold">No member record found.</td>
                  </tr>
                ) : (
                  filteredAndSortedReports.map((r, idx) => (
                    <tr key={r.id} className={`transition border-b ${appTheme === 'light' ? 'hover:bg-slate-50 border-slate-100' : 'hover:bg-[#0F172A]/80 border-slate-800/80'
                      }`}>
                      <td className="px-4 py-3.5 font-extrabold text-xs">
                        {idx === 0 ? (
                          <span className="px-2.5 py-1 rounded-lg bg-amber-500/20 text-amber-700 border border-amber-500/40 font-black shadow-xs">🥇 #1</span>
                        ) : idx === 1 ? (
                          <span className="px-2.5 py-1 rounded-lg bg-slate-200 text-slate-800 border border-slate-300 font-black shadow-xs">🥈 #2</span>
                        ) : idx === 2 ? (
                          <span className="px-2.5 py-1 rounded-lg bg-amber-100 text-amber-900 border border-amber-300 font-black shadow-xs">🥉 #3</span>
                        ) : (
                          <span className={appTheme === 'light' ? 'text-slate-500 font-bold' : 'text-slate-400 font-bold'}>#{idx + 1}</span>
                        )}
                      </td>

                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <img
                            src={r.profile ? (r.profile.startsWith('http') ? r.profile : `https://rmbbackend.rmbjaipur.com/${r.profile.replace(/\\/g, '/')}`) : 'https://via.placeholder.com/40'}
                            alt={r.name}
                            className="w-10 h-10 rounded-full object-cover border-2 border-[#FDBC12] shadow-md flex-shrink-0"
                            onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/40'; }}
                          />
                          <div>
                            <p className={`font-extrabold text-xs tracking-wide ${appTheme === 'light' ? 'text-slate-900' : 'text-white'}`}>{r.name}</p>
                            <p className={`text-[10px] font-semibold ${appTheme === 'light' ? 'text-slate-500' : 'text-slate-400'}`}>{r.designation || 'Member'}</p>
                          </div>
                        </div>
                      </td>

                      <td className={`px-4 py-3.5 font-bold text-xs ${appTheme === 'light' ? 'text-slate-700' : 'text-slate-300'}`}>{r.company_name || 'RMB Jaipur'}</td>

                      {(reportType === 'ALL' || reportType === 'R2R') && (
                        <td className="px-4 py-3.5 text-center">
                          <span className="px-3 py-1.5 rounded-xl font-extrabold text-xs bg-indigo-950/80 text-indigo-300 border border-indigo-500/40 shadow-xs inline-block">
                            🤝 {r.r2rCount} R2Rs
                          </span>
                        </td>
                      )}

                      {(reportType === 'ALL' || reportType === 'BUSINESS') && (
                        <>
                          <td className="px-4 py-3.5 text-right font-black text-xs text-emerald-400">
                            ₹{r.businessGivenAmount.toLocaleString('en-IN')}
                            <div className="text-[10px] text-slate-400 font-normal mt-0.5">({r.businessGivenCount} Transactions)</div>
                          </td>

                          <td className="px-4 py-3.5 text-right font-black text-xs text-purple-300">
                            ₹{r.businessTakenAmount.toLocaleString('en-IN')}
                            <div className="text-[10px] text-slate-400 font-normal mt-0.5">({r.businessTakenCount} Transactions)</div>
                          </td>
                        </>
                      )}

                      {(reportType === 'ALL' || reportType === 'POINTS') && (
                        <td className="px-4 py-3.5 text-center">
                          <div className="inline-block px-3.5 py-1.5 rounded-xl bg-amber-950/90 border border-amber-500/40 text-center shadow-md">
                            <span className="font-black text-xs text-[#FDBC12] block tracking-wide">
                              🏆 {r.totalPoints} Pts
                            </span>
                            <div className="text-[10px] font-bold text-slate-300 mt-1 space-x-2 flex items-center justify-center">
                              <span className="text-amber-300" title="Points from Meeting Attendance">📅 Att: {r.attendancePoints || 0}</span>
                              <span className="text-slate-600">•</span>
                              <span className="text-blue-300" title="Points from Activities (R2R, Referrals, etc.)">🤝 Act: {r.activityPoints || 0}</span>
                            </div>
                          </div>
                        </td>
                      )}

                      {(reportType === 'ALL' || reportType === 'ATTENDANCE') && (
                        <>
                          <td className="px-4 py-3.5 text-center font-extrabold text-xs text-slate-300">
                            {r.presentCount + r.substituteCount} / {r.totalMeetings}
                          </td>

                          <td className="px-4 py-3.5 text-center">
                            <span className={`px-3 py-1 rounded-full font-black text-xs shadow-xs ${r.attendancePercentage >= 75
                                ? 'bg-emerald-950/90 text-emerald-300 border border-emerald-500/40'
                                : r.attendancePercentage >= 50
                                  ? 'bg-amber-950/90 text-amber-300 border border-amber-500/40'
                                  : 'bg-rose-950/90 text-rose-300 border border-rose-500/40'
                              }`}>
                              {r.attendancePercentage}%
                            </span>
                          </td>
                        </>
                      )}
                      <td className="px-4 py-3.5 text-right">
                        <button
                          onClick={() => handleOpenMemberHistory(r)}
                          className="px-3.5 py-1.5 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-xs rounded-xl shadow-md transition flex items-center gap-1.5 ml-auto cursor-pointer"
                        >
                          <FaHistory /> View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MEMBER ACTIVITY & VERIFICATION HISTORY MODAL */}
      {selectedMember && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={() => setSelectedMember(null)}>
          <div className="bg-[#1E2B45] text-slate-100 rounded-2xl border border-[#2A3B5C] shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-[#0A2451] p-5 border-b border-[#2A3B5C] flex justify-between items-center">
              <div className="flex items-center gap-3">
                <img
                  src={selectedMember.profile ? (selectedMember.profile.startsWith('http') ? selectedMember.profile : `https://rmbbackend.rmbjaipur.com/${selectedMember.profile.replace(/\\/g, '/')}`) : `https://api.dicebear.com/7.x/avataaars/svg?seed=${selectedMember.name}`}
                  alt={selectedMember.name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-[#FDBC12]"
                  onError={(e) => { e.currentTarget.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${selectedMember.name}`; }}
                />
                <div>
                  <h2 className="text-lg font-black text-white flex items-center gap-2">
                    {selectedMember.name}
                  </h2>
                  <p className="text-xs text-slate-300 font-medium">
                    {selectedMember.company_name || 'RMB Member'} • {selectedMember.designation || 'Member'} • {selectedMember.number || 'No Mobile'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => window.print()}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded-lg shadow flex items-center gap-1 cursor-pointer transition"
                >
                  <FaPrint /> Print Summary
                </button>
                <div className="px-3 py-1.5 rounded-full font-black text-xs bg-amber-950 text-[#FDBC12] border border-amber-500/40">
                  🏆 {displayTotalPoints || selectedMember.totalPoints} Total Points
                </div>
                <button
                  onClick={() => setSelectedMember(null)}
                  className="w-8 h-8 bg-slate-800 hover:bg-rose-600 text-white rounded-full flex items-center justify-center transition cursor-pointer"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            {/* Points Bifurcation Strip */}
            <div className="px-5 py-2.5 bg-[#0D1525] border-b border-[#2A3B5C] flex flex-wrap items-center justify-between gap-2 text-xs">
              <span className="font-extrabold text-[#FDBC12] uppercase tracking-wider text-[11px] flex items-center gap-1">
                ⚡ Points Bifurcation Breakdown:
              </span>
              <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold">
                <span className="px-2.5 py-1 rounded-lg bg-amber-950 text-amber-300 border border-amber-500/30">
                  📅 Meeting Attendance: <strong className="text-white ml-0.5">+{selectedMember?.attendancePoints ?? totalAttendancePoints} pts</strong>
                </span>
                <span className="px-2.5 py-1 rounded-lg bg-blue-950 text-blue-300 border border-blue-500/30">
                  🤝 R2Rs / Host / Guest: <strong className="text-white ml-0.5">+{selectedMember?.r2rPoints ?? totalR2RPoints} pts</strong>
                </span>
                <span className="px-2.5 py-1 rounded-lg bg-purple-950 text-purple-300 border border-purple-500/30">
                  💼 Referrals: <strong className="text-white ml-0.5">+{selectedMember?.referralPoints ?? totalReferralPoints} pts</strong>
                </span>
                <span className="px-2.5 py-1 rounded-lg bg-cyan-950 text-cyan-300 border border-cyan-500/30">
                  🎟️ Visitors: <strong className="text-white ml-0.5">+{selectedMember?.visitorPoints ?? totalVisitorPoints} pts</strong>
                </span>
                <span className="px-2.5 py-1 rounded-lg bg-indigo-950 text-indigo-300 border border-indigo-500/30">
                  📍 Field Visits: <strong className="text-white ml-0.5">+{selectedMember?.fieldVisitPoints ?? totalFieldVisitPoints} pts</strong>
                </span>
              </div>
            </div>

            {/* Member Summary Stats Strip */}
            <div className="p-4 bg-[#131E32] border-b border-[#2A3B5C] grid grid-cols-2 md:grid-cols-7 gap-2.5 text-center text-xs">
              <div className="bg-[#1E2B45] p-2 rounded-xl border border-[#2A3B5C]">
                <p className="text-slate-400 text-[10px] uppercase font-bold">R2R Meetings</p>
                <p className="text-sm font-black text-blue-400">🤝 {selectedMember.r2rCount}</p>
              </div>
              <div className="bg-[#1E2B45] p-2 rounded-xl border border-[#2A3B5C]">
                <p className="text-slate-400 text-[10px] uppercase font-bold">Business Given</p>
                <p className="text-sm font-black text-emerald-400">₹{selectedMember.businessGivenAmount.toLocaleString('en-IN')}</p>
              </div>
              <div className="bg-[#1E2B45] p-2 rounded-xl border border-[#2A3B5C]">
                <p className="text-slate-400 text-[10px] uppercase font-bold">Business Received</p>
                <p className="text-sm font-black text-purple-300">₹{selectedMember.businessTakenAmount.toLocaleString('en-IN')}</p>
              </div>
              <div className="bg-[#1E2B45] p-2 rounded-xl border border-[#2A3B5C]">
                <p className="text-slate-400 text-[10px] uppercase font-bold">Referrals Passed</p>
                <p className="text-sm font-black text-indigo-300">💼 {selectedMember.referralsGivenCount}</p>
              </div>
              <div className="bg-[#1E2B45] p-2 rounded-xl border border-[#2A3B5C]">
                <p className="text-slate-400 text-[10px] uppercase font-bold">Visitors Invited</p>
                <p className="text-sm font-black text-cyan-300">🎟️ {selectedMember.visitorsInvitedCount}</p>
              </div>
              <div className="bg-[#1E2B45] p-2 rounded-xl border border-[#2A3B5C]">
                <p className="text-slate-400 text-[10px] uppercase font-bold">Attendance %</p>
                <p className="text-sm font-black text-amber-300">📅 {selectedMember.attendancePercentage}%</p>
              </div>
              <div className="bg-[#1E2B45] p-2 rounded-xl border border-[#2A3B5C]">
                <p className="text-slate-400 text-[10px] uppercase font-bold">Attendance Pts</p>
                <p className="text-sm font-black text-[#FDBC12]">🏆 +{totalAttendancePoints} pts</p>
              </div>
            </div>

            {/* Category Filter Pills */}
            <div className="p-3 bg-[#1E2B45] border-b border-[#2A3B5C] flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold text-slate-400 mr-1">Category:</span>
              {[
                { id: 'ALL', label: 'All History' },
                { id: 'R2R', label: '🤝 R2Rs' },
                { id: 'BUSINESS', label: '💰 Closed Business' },
                { id: 'REFERRAL', label: '💼 Referrals' },
                { id: 'VISITOR_INVITE', label: '🎟️ Visitors' },
                { id: 'FIELD_VISIT', label: '📍 Field Visits' },
                { id: 'ATTENDANCE_LOGS', label: `📅 Attendance (${memberAttendanceLogs.length})` }
              ].map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setHistoryCategoryFilter(cat.id as any)}
                  className={`px-3 py-1 rounded-lg text-xs font-extrabold transition cursor-pointer ${historyCategoryFilter === cat.id
                      ? 'bg-[#FDBC12] text-[#0A2451] shadow'
                      : 'bg-[#131E32] text-slate-300 hover:bg-[#2A3B5C]'
                    }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Modal Body */}
            <div className="p-4 overflow-y-auto flex-1 space-y-3">
              {fetchingHistory ? (
                <div className="p-8 text-center text-slate-400 font-bold animate-pulse">
                  Fetching itemized history & attendance logs...
                </div>
              ) : historyCategoryFilter === 'ATTENDANCE_LOGS' ? (
                <div className="space-y-3">
                  {/* Attendance Summary Bar */}
                  <div className="p-3 bg-[#131E32] rounded-xl border border-[#2A3B5C] flex flex-wrap items-center justify-between gap-3 text-xs">
                    <div className="flex items-center gap-2 font-bold text-white">
                      <span>📅 Total Events Logged: <strong className="text-[#FDBC12]">{memberAttendanceLogs.length}</strong></span>
                    </div>
                    <div className="flex items-center gap-2 text-[11px] font-bold">
                      <span className="px-2 py-0.5 rounded bg-emerald-950 text-emerald-400 border border-emerald-500/40">
                        Present: {memberAttendanceLogs.filter(a => a.status === 'Present').length} (+10 pts ea)
                      </span>
                      <span className="px-2 py-0.5 rounded bg-amber-950 text-amber-300 border border-amber-500/40">
                        Late: {memberAttendanceLogs.filter(a => a.status === 'Late').length} (+5 pts ea)
                      </span>
                      <span className="px-2 py-0.5 rounded bg-cyan-950 text-cyan-300 border border-cyan-500/40">
                        Substitute: {memberAttendanceLogs.filter(a => a.status === 'Substituted' || a.status === 'Substitute').length} (+10 pts ea)
                      </span>
                      <span className="px-2 py-0.5 rounded bg-rose-950 text-rose-300 border border-rose-500/40">
                        Absent: {memberAttendanceLogs.filter(a => a.status === 'Absent').length} (0 pts)
                      </span>
                    </div>
                    <div className="font-extrabold text-[#FDBC12] text-xs">
                      Total Attendance Points: +{totalAttendancePoints} pts
                    </div>
                  </div>

                  {/* Attendance Logs Table */}
                  <div className="overflow-x-auto border border-[#2A3B5C] rounded-xl">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-[#0A2451] text-slate-300 uppercase font-extrabold border-b border-[#2A3B5C]">
                          <th className="p-3">Meeting Event & Date</th>
                          <th className="p-3">Attendance Status</th>
                          <th className="p-3">Substitute / Notes</th>
                          <th className="p-3 text-right">Attendance Points Given</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#2A3B5C] bg-[#131E32]">
                        {memberAttendanceLogs.length > 0 ? (
                          memberAttendanceLogs.map((att: any) => (
                            <tr key={att.id} className="hover:bg-[#1E2B45] transition">
                              <td className="p-3 font-bold text-white">
                                <div>{att.event_title || `Chapter Meeting #${att.event_id}`}</div>
                                <div className="text-[10px] text-slate-400 font-normal">
                                  {att.event_date ? new Date(att.event_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                                </div>
                              </td>

                              <td className="p-3">
                                {att.status === 'Present' ? (
                                  <span className="px-2.5 py-1 rounded-full text-xs font-black bg-emerald-950 text-emerald-400 border border-emerald-500/40">
                                    🟢 Present
                                  </span>
                                ) : att.status === 'Late' ? (
                                  <span className="px-2.5 py-1 rounded-full text-xs font-black bg-amber-950 text-amber-300 border border-amber-500/40">
                                    ⏰ Late Arrival
                                  </span>
                                ) : att.status === 'Substituted' || att.status === 'Substitute' ? (
                                  <span className="px-2.5 py-1 rounded-full text-xs font-black bg-cyan-950 text-cyan-300 border border-cyan-500/40">
                                    👥 Substitute Sent
                                  </span>
                                ) : (
                                  <span className="px-2.5 py-1 rounded-full text-xs font-black bg-rose-950 text-rose-300 border border-rose-500/40">
                                    🔴 Absent
                                  </span>
                                )}
                              </td>

                              <td className="p-3 text-slate-300">
                                {att.substitute_name ? (
                                  <div>
                                    <div className="font-bold text-cyan-300">👤 {att.substitute_name}</div>
                                    <div className="text-[10px] text-slate-400">{att.substitute_company} {att.substitute_phone ? `(${att.substitute_phone})` : ''}</div>
                                  </div>
                                ) : (
                                  <span className="text-slate-500 italic">{att.notes || 'No notes'}</span>
                                )}
                              </td>

                              <td className="p-3 text-right font-black text-[#FDBC12]">
                                {att.status === 'Present' ? '+10 pts' : att.status === 'Late' ? '+5 pts' : (att.status === 'Substituted' || att.status === 'Substitute') ? '+10 pts' : '0 pts'}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={4} className="p-6 text-center text-slate-400 font-bold">
                              No meeting attendance records logged for this member yet.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : filteredHistoryActivities.length === 0 ? (
                <div className="p-8 text-center text-slate-400 font-bold">
                  No activity history record found for this filter.
                </div>
              ) : (
                <div className="overflow-x-auto border border-[#2A3B5C] rounded-xl">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-[#0A2451] text-slate-300 uppercase font-extrabold border-b border-[#2A3B5C]">
                        <th className="p-3">Type & Date</th>
                        <th className="p-3">Partner / Details</th>
                        <th className="p-3 text-right">Amount (₹)</th>
                        <th className="p-3 text-center">Proof</th>
                        <th className="p-3 text-center">Status</th>
                        <th className="p-3 text-right">Points</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#2A3B5C] bg-[#131E32]">
                      {filteredHistoryActivities.map((act) => (
                        <tr key={act.id} className="hover:bg-[#1E2B45] transition">
                          <td className="p-3 font-bold text-white">
                            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase mb-1 ${act.type === 'R2R' ? 'bg-blue-900 text-blue-200' :
                                act.type === 'BUSINESS_ACK' ? 'bg-emerald-900 text-emerald-200' :
                                  act.type === 'REFERRAL' ? 'bg-purple-900 text-purple-200' :
                                    act.type === 'FIELD_VISIT' ? 'bg-indigo-900 text-indigo-200' :
                                      act.isAttendanceRecord ? 'bg-cyan-900 text-cyan-200' :
                                        'bg-amber-900 text-amber-200'
                              }`}>
                              {act.isAttendanceRecord ? 'ATTENDANCE' : act.type}
                            </span>
                            <div className="text-[11px] text-slate-400">{act.date || (act.created_at ? new Date(act.created_at).toISOString().split('T')[0] : 'N/A')}</div>
                          </td>

                          <td className="p-3">
                            <div className="font-bold text-slate-200">
                              {act.isAttendanceRecord ? (
                                <>
                                  <span className="text-cyan-400 font-extrabold mr-1.5">[Meeting]</span>
                                  {act.receiver_name}
                                </>
                              ) : act.type === 'R2R' ? (
                                <>
                                  <span className="text-amber-400 font-extrabold mr-1.5">
                                    [{Number(act.giver_id) === Number(selectedMember?.id) ? 'Visitor' : 'Host'}]
                                  </span>
                                  Host: {act.receiver_name || 'Member'} | Visitor: {act.giver_name || 'Member'}
                                </>
                              ) : act.type === 'BUSINESS_ACK' ? (
                                <>
                                  <span className="text-emerald-400 font-extrabold mr-1.5">
                                    [{Number(act.giver_id) === Number(selectedMember?.id) ? 'Giver' : 'Receiver'}]
                                  </span>
                                  Giver: {act.giver_name || 'Member'} ➔ Receiver: {act.receiver_name || 'Member'}
                                </>
                              ) : act.type === 'REFERRAL' ? (
                                <>
                                  <span className="text-purple-400 font-extrabold mr-1.5">[Referrer]</span>
                                  Referred By: {act.giver_name || 'Member'} ➔ {act.ref_person_name || 'Prospect'} {act.number ? `(${act.number})` : ''}
                                </>
                              ) : act.type === 'FIELD_VISIT' ? (
                                <>
                                  <span className="text-indigo-400 font-extrabold mr-1.5">
                                    [{Number(act.giver_id) === Number(selectedMember?.id) ? 'Visitor' : 'Host'}]
                                  </span>
                                  Host: {act.receiver_name || 'Location Host'} | Visitor: {act.giver_name || 'Member'}
                                </>
                              ) : (
                                <>
                                  <span className="text-blue-400 font-extrabold mr-1.5">[Inviter]</span>
                                  Invited By: {act.giver_name || 'Member'} ➔ {act.ref_person_name || act.receiver_name || 'Guest'}
                                </>
                              )}
                            </div>
                            {act.details && <p className="text-[11px] text-slate-400 italic mt-0.5">"{act.details}"</p>}
                          </td>

                          <td className="p-3 text-right font-black text-emerald-400">
                            {act.amount && Number(act.amount) > 0 ? `₹${Number(act.amount).toLocaleString('en-IN')}` : '-'}
                          </td>

                          <td className="p-3 text-center">
                            {(act.image || act.receipt_url) ? (
                              <button
                                onClick={() => setPreviewImage(act.image || act.receipt_url)}
                                className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-[11px] font-bold inline-flex items-center gap-1 cursor-pointer shadow"
                              >
                                <FaReceipt /> Proof Photo
                              </button>
                            ) : (
                              <span className="text-[11px] text-slate-500 font-semibold">No Image</span>
                            )}
                          </td>

                          <td className="p-3 text-center">
                            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase ${act.status === 'VERIFIED' ? 'bg-emerald-950 text-emerald-300 border border-emerald-500/40' :
                                act.status === 'REJECTED' ? 'bg-rose-950 text-rose-300 border border-rose-500/40' :
                                  'bg-amber-950 text-amber-300 border border-amber-500/40'
                              }`}>
                              {act.status}
                            </span>
                          </td>

                          <td className="p-3 text-right font-black text-[#FDBC12]">
                            +{act.points_awarded || 0} pts
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* IMAGE PREVIEW MODAL */}
      {previewImage && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" onClick={() => setPreviewImage(null)}>
          <div className="relative max-w-2xl w-full max-h-[85vh] bg-black p-2 rounded-2xl border border-slate-700 flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-2 right-2 text-white font-black text-sm bg-rose-600 hover:bg-rose-700 px-3 py-1 rounded-full cursor-pointer"
            >
              ✕ Close
            </button>
            <img
              src={previewImage.startsWith('http') ? previewImage : `https://rmbbackend.rmbjaipur.com/${previewImage.replace(/\\/g, '/')}`}
              alt="Proof Preview"
              className="max-h-[80vh] w-auto object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Points Rules Reference Modal */}
      <PointRulesReferenceModal isOpen={showRulesModal} onClose={() => setShowRulesModal(false)} />

    </div>
  );
};

export default ReportsAnalytics;
